import React, { createContext, useContext, useMemo } from 'react';
import type { IUserRepository } from './interfaces/IUserRepository';
import type { IClientRepository } from './interfaces/IClientRepository';
import type { IOnboardingRepository } from './interfaces/IOnboardingRepository';
import type { IAdminRepository } from './interfaces/IAdminRepository';
import type { IEmailTemplateRepository } from './interfaces/IEmailTemplateRepository';
import type { IEmailLogRepository } from './interfaces/IEmailLogRepository';
import type { IBusinessUnitRepository } from './interfaces/IBusinessUnitRepository';
import type { IClientAgentRepository } from './interfaces/IClientAgentRepository';
import type { IClientHierarchyRepository } from './interfaces/IClientHierarchyRepository';
import type { IConfigurationRepository } from './interfaces/IConfigurationRepository';
import type { IWebhookRepository } from './interfaces/IWebhookRepository';
import type { IOrganizationTypeRepository } from './interfaces/IOrganizationTypeRepository';
import type { IRoleTemplateRepository } from './interfaces/IRoleTemplateRepository';
import type { ISupplierRepository } from './interfaces/ISupplierRepository';
import type { IComplianceRepository } from './interfaces/IComplianceRepository';
import { MockUserRepository } from './mock/MockUserRepository';
import { MockClientRepository } from './mock/MockClientRepository';
import { MockOnboardingRepository } from './mock/MockOnboardingRepository';
import { MockAdminRepository } from './mock/MockAdminRepository';
import { MockEmailTemplateRepository } from './mock/MockEmailTemplateRepository';
import { MockEmailLogRepository } from './mock/MockEmailLogRepository';
import { SupabaseUserRepository } from './supabase/SupabaseUserRepository';
import { SupabaseClientRepository } from './supabase/SupabaseClientRepository';
import { SupabaseOnboardingRepository } from './supabase/SupabaseOnboardingRepository';
import { SupabaseAdminRepository } from './supabase/SupabaseAdminRepository';
import { SupabaseEmailTemplateRepository } from './supabase/SupabaseEmailTemplateRepository';
import { SupabaseEmailLogRepository } from './supabase/SupabaseEmailLogRepository';
import { SupabaseBusinessUnitRepository } from './supabase/SupabaseBusinessUnitRepository';
import { SupabaseClientAgentRepository } from './supabase/SupabaseClientAgentRepository';
import { SupabaseClientHierarchyRepository } from './supabase/SupabaseClientHierarchyRepository';
import { SupabaseConfigurationRepository } from './supabase/SupabaseConfigurationRepository';
import { SupabaseWebhookRepository } from './supabase/SupabaseWebhookRepository';
import { SupabaseOrganizationTypeRepository } from './supabase/SupabaseOrganizationTypeRepository';
import { SupabaseRoleTemplateRepository } from './supabase/SupabaseRoleTemplateRepository';
import { SupabaseSupplierRepository } from './supabase/SupabaseSupplierRepository';
import { SupabaseComplianceRepository } from './supabase/SupabaseComplianceRepository';

interface Repositories {
  users: IUserRepository;
  clients: IClientRepository;
  onboarding: IOnboardingRepository;
  admin: IAdminRepository;
  emailTemplates: IEmailTemplateRepository;
  emailLogs: IEmailLogRepository;
  businessUnitRepository: IBusinessUnitRepository;
  clientAgentRepository: IClientAgentRepository;
  clientHierarchy: IClientHierarchyRepository;
  configuration: IConfigurationRepository;
  webhook: IWebhookRepository;
  organizationType: IOrganizationTypeRepository;
  roleTemplate: IRoleTemplateRepository;
  supplierRepository: ISupplierRepository;
  supplier: ISupplierRepository; // Alias for convenience
  compliance: IComplianceRepository;
}

const RepositoryContext = createContext<Repositories | undefined>(undefined);

interface RepositoryProviderProps {
  children: React.ReactNode;
  useMockData?: boolean;
}

export const RepositoryProvider: React.FC<RepositoryProviderProps> = ({ 
  children, 
  useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true' 
}) => {
  const repositories = useMemo<Repositories>(() => {
    if (useMockData) {
      // Use mock implementations
    return {
      users: new MockUserRepository(),
      clients: new MockClientRepository(),
      onboarding: new MockOnboardingRepository(),
        admin: new MockAdminRepository(),
        emailTemplates: new MockEmailTemplateRepository(),
        emailLogs: new MockEmailLogRepository(),
      businessUnitRepository: new SupabaseBusinessUnitRepository(), // No mock yet
      clientAgentRepository: new SupabaseClientAgentRepository(), // No mock yet
      clientHierarchy: new SupabaseClientHierarchyRepository(),
      configuration: new SupabaseConfigurationRepository(),
      webhook: new SupabaseWebhookRepository(),
      organizationType: new SupabaseOrganizationTypeRepository(),
      roleTemplate: new SupabaseRoleTemplateRepository(),
      supplierRepository: new SupabaseSupplierRepository(),
      supplier: new SupabaseSupplierRepository(), // Alias
      compliance: new SupabaseComplianceRepository(),
    };
  }

  // Use real Supabase implementations
  return {
      users: new SupabaseUserRepository(),
      clients: new SupabaseClientRepository(),
      onboarding: new SupabaseOnboardingRepository(),
      admin: new SupabaseAdminRepository(),
      emailTemplates: new SupabaseEmailTemplateRepository(),
      emailLogs: new SupabaseEmailLogRepository(),
    businessUnitRepository: new SupabaseBusinessUnitRepository(),
    clientAgentRepository: new SupabaseClientAgentRepository(),
    clientHierarchy: new SupabaseClientHierarchyRepository(),
    configuration: new SupabaseConfigurationRepository(),
    webhook: new SupabaseWebhookRepository(),
    organizationType: new SupabaseOrganizationTypeRepository(),
    roleTemplate: new SupabaseRoleTemplateRepository(),
    supplierRepository: new SupabaseSupplierRepository(),
    supplier: new SupabaseSupplierRepository(), // Alias
    compliance: new SupabaseComplianceRepository(),
  };
  }, [useMockData]);

  return (
    <RepositoryContext.Provider value={repositories}>
      {children}
    </RepositoryContext.Provider>
  );
};

export const useRepositories = (): Repositories => {
  const context = useContext(RepositoryContext);
  if (!context) {
    throw new Error('useRepositories must be used within a RepositoryProvider');
  }
  return context;
};
